import { ROLES } from '@app/permissions/roles';
import { selectCurrentUserRole } from '@features/auth/store/selectors';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import {
    ACADEMY_NODES_LIST,
    EXTENDED_ZONES_LIST,
    LABELS_ACADEMY_NODES_LIST,
    LABELS_OTHER_NODES_LIST,
    OTHER_NODES_LIST,
    UG_NODES_LIST,
    ZONES_LIST,
} from '@pages/schemes/constants';
import { default as classNames, default as cn } from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const FlowSidebar = () => {
    const [isSidebarOpened, setIsSidebarOpened] = useState(true);
    const { t } = useTranslation();
    const role = useSelector(selectCurrentUserRole);
    const theme = useTheme();

    const onDragStart = (event, nodeType) => {
        event.dataTransfer.setData('application/Flow', nodeType);
        event.dataTransfer.effectAllowed = 'move';
    };

    return (
        <aside className={cn('reactflow__sidebar', { collapsed: !isSidebarOpened })}>
            <IconButton className="reactflow__sidebar-collapse-btn" onClick={() => setIsSidebarOpened((prev) => !prev)}>
                <MenuOpenIcon
                    data-testid="collapse-sidebar-btn"
                    className={cn('reactflow__sidebar-collapse-btn-icon', { opened: isSidebarOpened })}
                />
            </IconButton>
            {isSidebarOpened && (
                <>
                    <Box className="reactflow__sidebar-description">{t('you_can_drag_these_nodes_to_the_pane')}</Box>

                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary, fontWeight: '700', margin: '10px 5px 5px' }}
                    >
                        UserGate:
                    </Typography>
                    <Box className="reactflow__sidebar-box">
                        {UG_NODES_LIST.map((node) => (
                            <Box key={node}>
                                <Box
                                    sx={{ width: '40px' }}
                                    id={`${node}-node`}
                                    className={`reactflow__${node}-image reactflow__node-image`}
                                    onDragStart={(event) => onDragStart(event, node)}
                                    draggable
                                />
                                <Typography variant="body2" htmlFor={`${node}-node`} sx={{ textAlign: 'center' }}>
                                    {node.toUpperCase()}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary, fontWeight: '700', margin: '10px 5px 5px' }}
                    >
                        {t('others')}:
                    </Typography>

                    <Box className="reactflow__sidebar-box">
                        {OTHER_NODES_LIST.map((node) => {
                            const nodeName =
                                node === 'trex' ? (theme.palette.mode === 'dark' ? 'trex-light' : 'trex-dark') : node;

                            return (
                                <Box key={node}>
                                    <Box
                                        sx={{
                                            height: '60px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box
                                            id={`${node}-node`}
                                            className={`reactflow__${nodeName}-image reactflow__node-image`}
                                            onDragStart={(event) => onDragStart(event, node)}
                                            draggable
                                        />
                                        <Typography
                                            variant="body2"
                                            htmlFor={`${node}-node`}
                                            sx={{ textAlign: 'center' }}
                                        >
                                            {LABELS_OTHER_NODES_LIST[node]}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                        {(role === ROLES.TEACHER || role === ROLES.STUDENT) &&
                            ACADEMY_NODES_LIST.map((node) => (
                                <Box key={node}>
                                    <Box
                                        sx={{
                                            height: '60px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box
                                            id={`${node}-node`}
                                            className={`reactflow__${node}-image reactflow__node-image`}
                                            onDragStart={(event) => onDragStart(event, node)}
                                            draggable
                                        />
                                        <Typography
                                            variant="body2"
                                            htmlFor={`${node}-node`}
                                            sx={{ textAlign: 'center' }}
                                        >
                                            {LABELS_ACADEMY_NODES_LIST[node]}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                    </Box>

                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary, fontWeight: '700', margin: '10px 5px 5px' }}
                    >
                        {t('zones')}:
                    </Typography>
                    <Box className="reactflow__sidebar-box" sx={{ marginBottom: '20px' }}>
                        {EXTENDED_ZONES_LIST.map((zone) => {
                            const className = ZONES_LIST.includes(zone)
                                ? 'reactflow__cloud-image'
                                : 'reactflow__dark-cloud-image';
                            return (
                                <Box
                                    key={zone}
                                    className={className}
                                    onDragStart={(event) => onDragStart(event, zone)}
                                    draggable
                                >
                                    <Box className={classNames('reactflow__zone-title', zone)}>
                                        {zone.toUpperCase()}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </aside>
    );
};

FlowSidebar.propTypes = {
    editMode: PropTypes.bool,
    nodes: PropTypes.array,
    edges: PropTypes.array,
    handleClickOpen: PropTypes.func,
    handleImportClick: PropTypes.func,
};

export default FlowSidebar;
